<script setup lang="ts">
const { userRole } = useAuth()
</script>

<template>
  <nav
    :aria-label="$t('navigation.settings.index')"
    class="flex flex-col gap-6"
  >
    <template v-if="userRole == 'admin'">
      <div>
        <h2 class="mb-2 px-2 text-xs font-semibold text-zinc-500">
          {{ $t("navigation.configuration") }}
        </h2>
        <div class="flex flex-col gap-0.5">
          <SidebarNavigationItem
            :to="{ name: 'settings-configuration-e-learning' }"
          >
            <SvgoPikaTraining />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.e_learning") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            v-if="$hasFeature('admin:simulations')"
            data-testid="settings-simulations-link"
            :to="{ name: 'settings-configuration-simulations' }"
          >
            <SvgoPikaSimulation />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.simulations") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            v-if="
              $hasFeature('admin:threats') ||
              $hasFeature('admin:security:threat-management') ||
              $hasFeature('admin:security:threat-forwarding')
            "
            data-testid="settings-threat-link"
            :to="{ name: 'settings-configuration-threats' }"
          >
            <SvgoPikaThreat />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.threats") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            v-if="
              $hasFeature('admin:breaches') ||
              $hasFeature('admin:security:data-breach-monitoring')
            "
            data-testid="settings-breaches-link"
            :to="{ name: 'settings-configuration-breaches' }"
          >
            <SvgoPikaBreach />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.breaches") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            :to="{ name: 'settings-configuration-interventions' }"
          >
            <SvgoPikaLightning />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.interventions") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            :to="{ name: 'settings-configuration-integrations' }"
          >
            <SvgoPikaIntegration />
            <SidebarNavigationItemText>
              {{ $t("navigation.integrations") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            v-if="$hasFeature('admin:platform:goal-setting')"
            :to="{ name: 'settings-configuration-goals' }"
          >
            <SvgoPikaFocus />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.goals") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
        </div>
      </div>
      <div>
        <h2 class="mb-2 px-2 text-xs font-semibold text-zinc-500">
          {{ $t("navigation.settings.administration") }}
        </h2>
        <div class="flex flex-col gap-0.5">
          <SidebarNavigationItem
            :to="{ name: 'settings-administration-workspace' }"
          >
            <SvgoPikaSettings />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.workspace") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            :to="{ name: 'settings-administration-authentication' }"
          >
            <SvgoPikaLock />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.authentication") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            :to="{ name: 'settings-administration-domains' }"
          >
            <SvgoPikaDomain />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.domains") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            v-if="$hasFeature('admin:platform:access-role-management')"
            data-testid="settings-admins-link"
            :to="{ name: 'settings-administration-admins' }"
          >
            <SvgoPikaUserSecurity />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.admins") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            v-if="
              $hasFeature('admin:managers') ||
              $hasFeature('admin:analytics:organizational-reporting')
            "
            data-testid="settings-reporting-link"
            :to="{ name: 'settings-administration-reporting' }"
          >
            <SvgoPikaBarChart />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.reporting") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            v-if="$hasFeature('admin:security:logs')"
            data-testid="settings-audit-logs-link"
            :to="{ name: 'settings-administration-audit-logs' }"
          >
            <SvgoPikaListChecked />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.audit_logs") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            :to="{ name: 'settings-administration-apis-and-data' }"
          >
            <SvgoPikaCode />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.apis_and_data") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
          <SidebarNavigationItem
            v-if="$hasFeature('admin:analytics:export')"
            :to="{ name: 'settings-administration-exports' }"
          >
            <SvgoPikaDownload />
            <SidebarNavigationItemText>
              {{ $t("navigation.settings.exports") }}
            </SidebarNavigationItemText>
          </SidebarNavigationItem>
        </div>
      </div>
    </template>
    <div v-else-if="$hasFeature('admin:platform:goal-setting')">
      <h2 class="mb-2 px-2 text-xs font-semibold text-zinc-500">
        {{ $t("navigation.configuration") }}
      </h2>
      <div class="flex flex-col gap-0.5">
        <SidebarNavigationItem :to="{ name: 'settings-configuration-goals' }">
          <SvgoPikaFocus />
          <SidebarNavigationItemText>
            {{ $t("navigation.settings.goals") }}
          </SidebarNavigationItemText>
        </SidebarNavigationItem>
      </div>
    </div>
    <div>
      <h2 class="mb-2 px-2 text-xs font-semibold text-zinc-500">
        {{ $t("navigation.settings.personal") }}
      </h2>
      <div class="flex flex-col gap-0.5">
        <SidebarNavigationItem
          :to="{ name: 'settings-personal-security-and-access' }"
        >
          <SvgoPikaUser />
          <SidebarNavigationItemText>
            {{ $t("navigation.user_settings") }}
          </SidebarNavigationItemText>
        </SidebarNavigationItem>
      </div>
    </div>
  </nav>
</template>
